import React, { useEffect, useState } from 'react'
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import toast from 'react-hot-toast'
import { verifyEmail, resendVerification, errorCode } from 'services/auth.service'
import AuthLayout, { authButtonClass } from 'components/AuthLayout'
import CodeInput, { CODE_LENGTH, emptyCode } from 'components/CodeInput'
import ResendCode from 'components/ResendCode'
import { useI18n, apiError } from 'i18n'

const VerifyEmail = () => {
  const navigate = useNavigate()
  const { state } = useLocation()
  const [params] = useSearchParams()
  const email = params.get('email') || ''
  const { t } = useI18n()
  const [digits, setDigits] = useState(emptyCode)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => { if (state?.resent) toast.success(t('verify.resentOnLogin'), { id: 'verify-resent' }) }, [state, t])

  if (!email) return <Navigate to='/signup' replace />

  const submit = async (code) => {
    if (busy || code.length !== CODE_LENGTH) return
    setBusy(true)
    setError('')
    try {
      await verifyEmail(email, code)
      toast.success(t('verify.welcome'))
      navigate('/', { replace: true })
    } catch (err) {
      if (errorCode(err) === 'ALREADY_VERIFIED') {
        toast(t('verify.alreadyVerified'))
        navigate('/login', { replace: true })
        return
      }
      setError(apiError(err, t('verify.failed')))
      setDigits(emptyCode())
    } finally {
      setBusy(false)
    }
  }

  const onResend = async () => {
    try {
      await resendVerification(email)
      toast.success(t('verify.resent'))
      setError('')
      return true
    } catch (err) {
      toast.error(apiError(err, t('verify.resendFailed')))
      return false
    }
  }

  return (
    <AuthLayout
      subtitle={<>{t('verify.sentTo')} <strong className='text-slate-800'>{email}</strong></>}
      onSubmit={(e) => { e.preventDefault(); submit(digits.join('')) }}
      footer={<>{t('verify.wrongEmail')} <Link to='/signup' className='font-semibold text-accent hover:underline'>{t('verify.signupAgain')}</Link></>}
    >
      <CodeInput digits={digits} onChange={(next) => { setDigits(next); setError('') }} onComplete={submit} error={error} disabled={busy} />
      <p role='alert' className='mt-3 min-h-[1.25rem] text-center text-sm text-status-failed'>{error}</p>
      <p className='mb-5 text-center text-xs text-slate-500'>{t('verify.expires')}</p>

      <button type='submit' disabled={busy || digits.some((d) => !d)} className={authButtonClass}>
        {busy ? t('verify.verifying') : t('verify.submit')}
      </button>
      <ResendCode onResend={onResend} />
    </AuthLayout>
  )
}

export default VerifyEmail
